import React, { useRef, useEffect, useState } from 'react';
import Hls from 'hls.js';

interface VideoPlayerProps {
  videoUrl: string;
  isYoutubeUrl?: boolean;
  thumbnail?: string;
  width?: string;
  height?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  isYoutubeUrl = false,
  thumbnail = '',
  width = '100%',
  height = '200'
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(false);

  const getDropboxRawUrl = (url: string) => {
    if (!url) return url;

    if (url.includes('dropbox.com')) {
      return url
        .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
        .replace('?dl=0', '')
        .replace('&dl=0', '');
    }

    return url;
  };

  const safeThumbnail = getDropboxRawUrl(thumbnail);

  useEffect(() => {
    let hls: Hls | null = null;
    const video = videoRef.current;

    if (!video || !videoUrl) return;

    setUnsupported(false);
    setShowThumbnail(false);

    if (videoUrl.includes('.m3u8')) {
      if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(videoUrl);
        hls.attachMedia(video);
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = videoUrl;
      } else {
        setUnsupported(true);
      }
    } else if (!isYoutubeUrl) {
      video.src = videoUrl;
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (hls) {
        hls.destroy();
      }

      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    };
  }, [videoUrl, isYoutubeUrl]);

  if (!videoUrl) {
    return thumbnail ? (
      <div style={{ width, height, position: 'relative' }}>
        <img
          src={safeThumbnail}
          alt="Video thumbnail"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px'
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              background: 'rgba(0,0,0,0.6)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 18
            }}
          >
            ▶
          </div>
        </div>
      </div>
    ) : (
      <div style={{ color: '#999' }}>No video available</div>
    );
  }

  if (unsupported) {
    return thumbnail ? (
      <img
        src={safeThumbnail}
        alt="thumbnail"
        style={{
          width,
          height,
          objectFit: 'cover',
          borderRadius: '8px'
        }}
      />
    ) : (
      <div>
        <p style={{ color: '#999' }}>
          Preview not available for this format.
        </p>

        <a href={videoUrl} target="_blank" rel="noreferrer">
          Download video
        </a>
      </div>
    );
  }

  return (
    <div style={{ width, position: 'relative' }}>
      {loading && <div>Loading video...</div>}

      {isYoutubeUrl ? (
        <iframe
          width={width}
          height={height}
          src={videoUrl}
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title="YouTube Video"
        />
      ) : showThumbnail && thumbnail ? (
        <div
          style={{
            width: '100%',
            height,
            position: 'relative',
            cursor: 'pointer'
          }}
          onClick={() => setShowThumbnail(false)}
        >
          <img
            src={safeThumbnail}
            alt="Video thumbnail"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px'
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              style={{
                width: 60,
                height: 60,
                background: 'rgba(0,0,0,0.6)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 22
              }}
            >
              ▶
            </div>
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          controls
          width={width}
          height={height}
          preload="metadata"
          playsInline
          poster={safeThumbnail}
          onWaiting={() => setLoading(true)}
          onPlaying={() => {
            setLoading(false);
            setShowThumbnail(false);
          }}
          onLoadedData={() => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = window.setTimeout(() => {
              const video = videoRef.current;

              if (!video || video.videoWidth === 0) {
                setShowThumbnail(true);
              }
            }, 500);
          }}
          onError={() => {
            setUnsupported(true);
            setShowThumbnail(true);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(VideoPlayer);